import { supabase } from '../../lib/supabase';

export interface TrendWeek { start: string; received: number; approved: number; rejected: number; medianDays: number | null; }
export interface DashboardTrends { weeks: TrendWeek[]; }
const day = 86400000;
// UTC dates represent local calendar dates, so weekly buckets use Mexico City boundaries.
function localDate(value: Date) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(value);
  const part = (type: string) => parts.find(p => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export function summarizeTrends(rows: { id: string; upload_date: string; approval_timestamp: string | null; status: string }[], now = new Date()): DashboardTrends {
  const today = new Date(`${localDate(now)}T00:00:00Z`);
  const end = today.getTime() - ((today.getUTCDay() + 6) % 7) * day;
  const start = end - 56 * day;
  const weeks: TrendWeek[] = Array.from({ length: 8 }, (_, i) => ({ start: new Date(start + i * 7 * day).toISOString().slice(0, 10), received: 0, approved: 0, rejected: 0, medianDays: null }));
  const durations: number[][] = weeks.map(() => []);
  const bucket = (value: string) => Math.floor((Date.parse(`${localDate(new Date(value))}T00:00:00Z`) - start) / (7 * day));
  const seen = new Set<string>();
  rows.forEach(row => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    const upload = Date.parse(row.upload_date);
    if (Number.isFinite(upload)) { const i = bucket(row.upload_date); if (i >= 0 && i < 8) weeks[i].received++; }
    const approved = Date.parse(row.approval_timestamp || '');
    if (row.status === 'rejected' && Number.isFinite(approved)) {
      const i = bucket(row.approval_timestamp!);
      if (i >= 0 && i < 8) weeks[i].rejected++;
    }
    if (row.status === 'approved' && Number.isFinite(approved)) {
      const i = bucket(row.approval_timestamp!);
      if (i >= 0 && i < 8) {
        weeks[i].approved++;
        if (Number.isFinite(upload) && approved >= upload) durations[i].push((approved - upload) / day);
      }
    }
  });
  durations.forEach((values, i) => {
    values.sort((a, b) => a - b);
    if (values.length) { const mid = Math.floor(values.length / 2); weeks[i].medianDays = values.length % 2 ? values[mid] : (values[mid - 1] + values[mid]) / 2; }
  });
  return { weeks };
}
export async function getDashboardTrends(): Promise<DashboardTrends> {
  const now = new Date();
  const start = summarizeTrends([], now).weeks[0].start;
  const rows: { id: string; upload_date: string; approval_timestamp: string | null; status: string }[] = [];
  for (let offset = 0; offset < 100000; offset += 1000) {
    const { data, error } = await supabase.from('documents')
      .select('id,upload_date,approval_timestamp,status,user_document_requirements!documents_requirement_id_fkey!inner(users!user_document_requirements_user_id_fkey!inner(is_internal,is_active,is_deleted))')
      .eq('is_deleted', false)
      .eq('user_document_requirements.users.is_internal', false)
      .eq('user_document_requirements.users.is_active', true)
      .eq('user_document_requirements.users.is_deleted', false)
      .or(`upload_date.gte.${start},approval_timestamp.gte.${start}`)
      .order('id').range(offset, offset + 999);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) return summarizeTrends(rows, now);
  }
  throw new Error('El volumen excede el límite del resumen');
}
